import { useCallback } from "react";
import { isNil, omit } from "lodash";
import qs from "query-string";
import { useHistory } from "react-router-dom";
import { toQueryString } from "@circle-react/helpers/urlHelpers";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";

export const useFilterUrl = () => {
  const history = useHistory();
  const queryParams = useRouterQueryParams();

  const updateUrl = useCallback(
    (
      queryParam: string = "",
      filterValue: string | number | null = "",
    ): void => {
      const urlSearchParams = new URLSearchParams(history.location.search);
      /* Not using updateOrRemoveQueryParam since `page` also has to be set here.
        updateOrRemoveQueryParam always fetches the latest queryParams from the url.
      */
      if (isNil(filterValue) || filterValue === "") {
        urlSearchParams.delete(queryParam);
      } else {
        urlSearchParams.set(queryParam, String(filterValue));
      }
      urlSearchParams.set("page", "1");
      history.push({
        search: urlSearchParams.toString(),
      });
    },
    [history],
  );

  const appendToUrl = (
    queryParam: string = "",
    filterValue: string = "",
  ): void => {
    queryParams[queryParam] = filterValue;
    queryParams.page = "1";
    history.push({ search: qs.stringify(queryParams) });
  };

  const removeAllQueryParams = useCallback((): void => {
    history.push({
      search: "",
    });
  }, [history]);

  const removeQueryParams = useCallback(
    (queryKeys: string[] = []): void => {
      const newParams = omit(queryParams, [...queryKeys, "page"]);
      const search = toQueryString(newParams);
      history.push({ search });
    },
    [queryParams, history],
  );

  const updateSorting = useCallback(
    (sortingState: { id: string; desc?: boolean } | null): void => {
      const urlSearchParams = new URLSearchParams(history.location.search);

      // Remove all existing order parameters
      const keysToDelete: string[] = [];
      urlSearchParams.forEach((_, key) => {
        if (key.startsWith("order[")) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => urlSearchParams.delete(key));

      if (sortingState) {
        urlSearchParams.set(
          `order[${sortingState.id}]`,
          sortingState.desc ? "desc" : "asc",
        );
      }

      // Reset to page 1 when sorting changes
      urlSearchParams.set("page", "1");

      history.push({
        search: urlSearchParams.toString(),
      });
    },
    [history],
  );

  return {
    history,
    queryParams,
    updateUrl,
    removeAllQueryParams,
    removeQueryParams,
    appendToUrl,
    updateSorting,
  };
};
